/* Warehouse Bold — footer newsletter: subscribe to Klaviyo AND keep Shopify's
 * own customer record.
 *
 * Owen, 2026-08-16: signups go to the "Email List" (single opt-in) and should
 * still become Shopify customers, as they always have.
 *
 * ⚠ The Shopify form is left ENTIRELY ALONE. It still posts natively to
 * /contact, so the customer is created and Shopify's own success state renders
 * exactly as before. All this adds is a second, parallel request to Klaviyo.
 * If Klaviyo is down, unreachable, or this script never loads, the form
 * behaves precisely as it did yesterday — the signup is never lost.
 *
 * ⚠ `keepalive: true` is the load-bearing detail. The native submit navigates
 * the page immediately, which normally kills in-flight fetches; keepalive tells
 * the browser to finish this one anyway. That is what lets the subscribe fire
 * WITHOUT delaying the shopper behind a network round trip. The alternative —
 * awaiting Klaviyo before submitting — puts their latency in front of our form.
 *
 * ⚠ No consent checkbox by design (Owen, 2026-08-16). The list's own opt-in
 * setting governs; "Email List" is single opt-in, so the profile lands
 * SUBSCRIBED. Changing that list to double opt-in in Klaviyo changes this
 * behaviour with no code change here — which is why the list id is a setting
 * rather than a constant.
 */
(() => {
  'use strict';

  const REVISION = '2024-10-15'; // Klaviyo pins its API by date; bump deliberately, not casually.

  const subscribe = (form, email) => {
    const company = form.dataset.klaviyoCompany;
    const list = form.dataset.klaviyoList;
    if (!company || !list || !email) return;

    const body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          // Shows on the profile as custom_method_detail, so signups from here
          // are tellable apart from the popup's in Klaviyo.
          custom_source: form.dataset.klaviyoSource || 'Footer newsletter'
        },
        relationships: { list: { data: { type: 'list', id: list } } }
      }
    };

    try {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: REVISION },
        body: JSON.stringify(body),
        keepalive: true
      }).catch(() => {
        /* Swallowed on purpose. The shopper is already on their way to the
           Shopify success page; a failed marketing sync must never surface as
           an error on a form that DID work. */
      });
    } catch (e) {
      /* Same reasoning — never let this break the submit. */
    }
  };

  const bind = (form) => {
    if (form.dataset.wbNewsletterBound) return;
    form.dataset.wbNewsletterBound = 'true';
    form.addEventListener('submit', () => {
      const input = form.querySelector('input[type="email"]');
      const email = input && input.value.trim();
      // No preventDefault anywhere: the native POST proceeds untouched.
      if (email) subscribe(form, email);
    });
  };

  const init = () => document.querySelectorAll('[data-wb-newsletter]').forEach(bind);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
